import {
  Device,
  ForecastSlice,
  useForecastTemplateLimits,
  useInputFieldLimits,
  useInputFieldRules,
  usePowerLevels,
  usePowerLevelsRules,
} from '@e2m-customer-portal/hooks';
import { InputFieldType } from '@e2m-customer-portal/shared';
import { eachMinuteOfInterval, endOfDay, Interval, startOfDay, startOfWeek } from 'date-fns';
import { equals } from 'ramda';
import React, { memo, MouseEvent, ReactElement, useCallback } from 'react';
import { ForecastProperty, MarketLocation, Resolution, SpotPriceForecast } from '../../../../../generated/graphql';
import { SelectedCell } from '../ForecastList';
import { SpotPriceResult } from '../spot-price';
import { switchFocusForDeviceMap } from '../utils';
import { SelectHandle } from './DeviceTable';
import { DeviceTableRow } from './DeviceTableRow';

export const DST_CORRECTION_ID = 'dst-correction';

interface DeviceTableBodyProps {
  marketLocation: MarketLocation;
  device: Device;
  interval: Interval;
  deadlineDate?: Date;
  slices: readonly ForecastSlice[];
  dstSliceValues?: Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>;
  daysInForecast: Date[];
  spotPriceForecast?: SpotPriceForecast;
  showSpotPrices?: boolean;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  readOnly?: boolean;
  resetTrigger: boolean;
  editedSlices: Set<string>;
  isHighlighted: boolean;
  overviewMap: Map<string, (HTMLInputElement | null)[][]>;
  selectHandle: SelectHandle;
  selectedCells: SelectedCell[];
  setRevokeDowntimeInterval: React.Dispatch<React.SetStateAction<Interval | null>>;
  setEditedSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setErroneousSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setIsHighlighted: React.Dispatch<React.SetStateAction<boolean>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, Array<Array<HTMLInputElement | null>>>>>;
  setSelectHandle: React.Dispatch<React.SetStateAction<SelectHandle>>;
  setSelectedCells: React.Dispatch<React.SetStateAction<SelectedCell[]>>;
  handleSelectAll: () => void;
  pasteCopiedCells: (event: React.KeyboardEvent<HTMLInputElement>, startRow: number, startColumn: number) => void;
  saveChanges: () => void;
  currentForecastProperty?: ForecastProperty;
  toggleDSTModal: () => void;
}

function DeviceTableBodyMemo({
  marketLocation,
  device,
  interval,
  deadlineDate,
  slices,
  dstSliceValues,
  daysInForecast,
  spotPriceForecast,
  showSpotPrices,
  spotPrice,
  readOnly,
  resetTrigger,
  editedSlices,
  isHighlighted,
  overviewMap,
  selectHandle,
  selectedCells,
  setRevokeDowntimeInterval,
  setEditedSlices,
  setErroneousSlices,
  setIsHighlighted,
  setOverviewMap,
  setSelectHandle,
  setSelectedCells,
  handleSelectAll,
  pasteCopiedCells,
  saveChanges,
  currentForecastProperty,
  toggleDSTModal,
}: Readonly<DeviceTableBodyProps>): ReactElement<DeviceTableBodyProps> {
  const limits = useInputFieldLimits(marketLocation.id, device.id, interval);
  const { maximum, minimum } = useForecastTemplateLimits(device.id, interval);
  const powerLevels = usePowerLevels(device.id) ?? [];
  const powerLevelRules = usePowerLevelsRules(powerLevels, 'kW');
  const rules = useInputFieldRules(slices[0].fields, limits, minimum, maximum)[InputFieldType.PRODUCTION];
  const isFifteenMinutesResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;

  const deviceMap = overviewMap.get(device.id);
  if (!deviceMap) throw new Error(`Data Error: Table overview for device with id "${device.id}" not found`);

  const switchFocus = useCallback(switchFocusForDeviceMap(deviceMap), [deviceMap]);

  const selectRow = useCallback(
    (index: number) => (event: MouseEvent) => {
      event.stopPropagation();
      setSelectedCells([]);

      let focusedColumn = null;
      let skippedColumns = 0;
      for (const column of deviceMap[index]) {
        if (column && !column.readOnly && !column.disabled) {
          column.focus();
          focusedColumn = column;
          break;
        }
        skippedColumns += 1;
      }
      if (!focusedColumn) return;

      const boundingRect = focusedColumn?.getBoundingClientRect();
      if (!boundingRect) return;

      setSelectHandle({
        active: true,
        cellWidth: boundingRect.width,
        cellHeight: boundingRect.height + 1,
        column: skippedColumns,
        row: index,
        targetColumn: deviceMap[0].length - 1,
        targetRow: index,
        selectionType: 'select',
      });
      event.preventDefault();
    },
    [deviceMap, setSelectHandle, setSelectedCells],
  );

  const expandRowSelection = useCallback(
    (index: number) => (event: MouseEvent) => {
      // Do nothing if the mouse is just moving casually
      if (!event.buttons) return;

      setSelectHandle((selectHandle) => ({
        ...selectHandle,
        targetRow: index,
      }));
    },
    [setSelectHandle],
  );

  // Find doubled DST slice
  const dstSliceID = slices.find(
    (slice, index) =>
      !isFifteenMinutesResolution &&
      index + daysInForecast.length < slices.length &&
      slice.date.getTimezoneOffset() < slices[index + daysInForecast.length].date.getTimezoneOffset(),
  )?.id;

  const getRowSlices = (date: Date, rowIndex: number) => {
    const hours = date.getHours();

    const rowSlices = slices.filter((slice) => {
      const sliceHours = slice.date.getHours();

      if (sliceHours !== hours) return false;

      return !isFifteenMinutesResolution || slice.date.getMinutes() === date.getMinutes();
      // Add an empty slice for DST shenanigans
    });
    if (rowSlices.length < daysInForecast.length) {
      const dstIndex =
        rowSlices.findLastIndex((slice, sliceIndex) => daysInForecast[sliceIndex].getDate() === slice.date.getDate()) +
        1;
      rowSlices.splice(dstIndex, 0, {
        date: new Date(daysInForecast[dstIndex].setHours(rowIndex)),
        fields: [],
        hasDowntime: false,
        id: DST_CORRECTION_ID,
      });
    }
    return rowSlices;
  };

  const timeUnits = eachMinuteOfInterval(
    {
      start: startOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
      end: endOfDay(startOfWeek(daysInForecast[0], { weekStartsOn: 1 })),
    },
    { step: isFifteenMinutesResolution ? 15 : 60 },
  );

  return (
    <tbody data-testid="device-table-body">
      {timeUnits.map((date, rowIndex) => {
        const rowSlices = getRowSlices(date, rowIndex);

        return (
          <DeviceTableRow
            key={`device-${device.id}-table-row-${date.toISOString()}`}
            marketLocation={marketLocation}
            device={device}
            interval={interval}
            rowSlices={rowSlices}
            date={date}
            deadlineDate={deadlineDate}
            rowIndex={rowIndex}
            resetTrigger={resetTrigger}
            editedSlices={editedSlices}
            isHighlighted={isHighlighted}
            overviewMap={overviewMap}
            selectHandle={selectHandle}
            selectedCells={selectedCells}
            dstSliceID={dstSliceID}
            dstSliceValues={dstSliceValues}
            powerLevelRules={powerLevelRules}
            rules={rules}
            setRevokeDowntimeInterval={setRevokeDowntimeInterval}
            setEditedSlices={setEditedSlices}
            setErroneousSlices={setErroneousSlices}
            setIsHighlighted={setIsHighlighted}
            setOverviewMap={setOverviewMap}
            setSelectHandle={setSelectHandle}
            setSelectedCells={setSelectedCells}
            handleSelectAll={handleSelectAll}
            pasteCopiedCells={pasteCopiedCells}
            saveChanges={saveChanges}
            switchFocus={switchFocus}
            selectRow={selectRow}
            expandRowSelection={expandRowSelection}
            currentForecastProperty={currentForecastProperty}
            spotPriceForecast={spotPriceForecast}
            showSpotPrices={showSpotPrices}
            spotPrice={spotPrice}
            readOnly={readOnly}
            toggleDSTModal={toggleDSTModal}
          />
        );
      })}
    </tbody>
  );
}

export const DeviceTableBody = memo(DeviceTableBodyMemo, equals);
